import { TriggerObject } from "../../../interfaces/models/hubspot";
import { useHubspotActions } from "../../../stores/hubspot/useHubspotActions";
import { useHubspotStore } from "../../../stores/hubspot/useHubspotStore";
import { useHubspotWebhook } from "../../apis/hubspot/useHubspotWebhook";
import { useConnectionsRequest } from "../../apis/connections/useConnectionsRequest";
import { useExtensionRequest } from "../../apis/extension/useExtensionRequest";
import { useCallback } from "react";

export const useHubspotHandlers = () => {
    const { checkHubSpotQueueLastRun, startHubSpotQueueInterval } = useExtensionRequest();
    const { 
        getHubspotAccessToken, 
        addHubspotAccessToken, 
        removeHubspotAccessToken, 
        getHubspotTriggers, 
        removeHubspotTrigger, 
        addHubspotTrigger, 
        editHubspotTrigger,
        getOAuthAuthorizationUrl,
        getOAuthStatus,
        reauthorizeWithOptionalScopes,
        disconnectOAuth,
        refreshOAuthToken,
        getContactProperties,
        saveFieldMappings
    } = useHubspotWebhook()
    const { bulkImportToHubspot } = useConnectionsRequest()
    const { setHubspotTriggers, setHubspotAccessToken } = useHubspotActions()

    const { store: { triggers } } = useHubspotStore()

    const setAccessTokenHandler = (accessToken: any) => {
        setHubspotAccessToken(accessToken)
    }

    const getHubspotAccessTokenHandler = async () => {
        const result = await getHubspotAccessToken()
        if(result) setHubspotAccessToken({ exists: true, ...result })
    }

    const addHubspotAccessTokenHandler = async (value: object) => {
        const result = await addHubspotAccessToken(value)
        if(result) setHubspotAccessToken({ ...result, exists: true })
    }

    const removeHubspotAccessTokenHandler = async (id: string) => {
        await removeHubspotAccessToken(id)
        setHubspotAccessToken({ exists: false, value: '' })
    }

    const getHubspotTriggersHandler = async () => {
        const result = await getHubspotTriggers()
        setHubspotTriggers(result?.response ?? [])
    }

    const addHubspotTriggerHandler = async (payload: any) => {
        const result = await addHubspotTrigger(payload)
        setHubspotTriggers([result, ...triggers])
    }

    const editHubspotTriggerHandler = async (payload: any) => {
        const id = payload.id
        const trigger = payload.body

        const newArray = triggers.map((item: TriggerObject) => {
            if(item._id === id) {
                return {...item, ...trigger}
            } else {
                return item
            }
        })
        setHubspotTriggers(newArray)

        await editHubspotTrigger(payload)
    }

    const removeHubspotTriggerHandler = async (id: any) => {
        const newArray = triggers.filter((trigger: TriggerObject) => trigger._id !== id)
        setHubspotTriggers(newArray)

        await removeHubspotTrigger(id)
    }

    const exportToHubspotHandler = async (payload: any): Promise<{ success: boolean; message: string; data?: any }> => {
       const result = await bulkImportToHubspot(payload);
       return result
    }

    // OAuth handlers
    const initiateOAuthHandler = async () => {
        const result = await getOAuthAuthorizationUrl()
        if (result?.authorizationUrl) {
            // Redirect user to HubSpot OAuth page
            window.location.href = result.authorizationUrl
        }
        return result
    }

    const getOAuthStatusHandler = async () => {
        const result = await getOAuthStatus()
        return result
    }

    const reauthorizeWithOptionalScopesHandler = async () => {
        const result = await reauthorizeWithOptionalScopes()
        if (result?.authorizationUrl) {
            // Redirect user to HubSpot OAuth page for re-authorization
            window.location.href = result.authorizationUrl
        }
        return result
    }

    const disconnectOAuthHandler = async () => {
        await disconnectOAuth()
        setHubspotAccessToken({ exists: false, value: '' })
    }

    const refreshOAuthTokenHandler = async () => {
        const result = await refreshOAuthToken()
        return result
    }

    const getContactPropertiesHandler = async () => {
        const result = await getContactProperties()
        return result
    }

    const saveFieldMappingsHandler = async (mappings: any) => {
        const result = await saveFieldMappings({ mappings })
        return result
    }

    /**
     * Check and start HubSpot queue interval if conditions are met
     * Similar to startPeriodicPostsCheck - handles all workspace/user checks internally
     */
    const startHubSpotQueueIntervalHandler = useCallback(async (workspace: any, user: any) => {
        // Check conditions: user logged in and workspace has hubspotEnabled
        if (!user || !workspace?.hubspotEnabled || !workspace?._id) {
            console.log('[HubSpot Queue] Not starting - missing conditions:', {
                hasUser: !!user,
                hubspotEnabled: workspace?.hubspotEnabled,
                hasWorkspaceId: !!workspace?._id
            });
            return;
        }

        const workspaceId = workspace._id.toString();
        
        // Check last run time from background script
        try {
            const checkResult = await checkHubSpotQueueLastRun({
                workspaceId: workspaceId
            });

            // Background.js sends: { result: { hasPassed, lastRunTime, minutesSinceLastRun, intervalMinutes, workspaceId } }
            // With all: true, checkResult = { result: { ... } }
            if (checkResult?.result) {
                const { hasPassed, lastRunTime, minutesSinceLastRun, intervalMinutes } = checkResult.result;

                // If no last run time OR it's been more than the interval, start the interval
                if (hasPassed) {
                    const validIntervalMinutes = intervalMinutes || 60;
                    console.log('[HubSpot Queue] Starting interval - last run:', lastRunTime ? `${minutesSinceLastRun} minutes ago` : 'never', `(interval: ${validIntervalMinutes} minutes)`);
                    
                    try {
                        await startHubSpotQueueInterval({
                            workspaceId: workspaceId,
                            intervalMinutes: validIntervalMinutes
                        });
                        console.log('[HubSpot Queue] Interval started successfully');
                    } catch (error) {
                        console.error('[HubSpot Queue] Error starting interval:', error);
                    }
                } else {
                    const validIntervalMinutes = intervalMinutes || 60;
                    console.log(`[HubSpot Queue] Skipping - last run was ${minutesSinceLastRun} minutes ago (less than ${validIntervalMinutes} minutes)`);
                }
            } else {
                console.error('[HubSpot Queue] Invalid response from background script:', checkResult);
            }
        } catch (error) {
            console.error('[HubSpot Queue] Error checking last run time:', error);
        }
    }, [checkHubSpotQueueLastRun, startHubSpotQueueInterval])

    return {
        setAccessTokenHandler,
        getHubspotAccessTokenHandler,
        addHubspotAccessTokenHandler,
        removeHubspotAccessTokenHandler,
        getHubspotTriggersHandler,
        addHubspotTriggerHandler,
        editHubspotTriggerHandler,
        removeHubspotTriggerHandler,
        exportToHubspotHandler,
        // OAuth handlers
        initiateOAuthHandler,
        getOAuthStatusHandler,
        reauthorizeWithOptionalScopesHandler,
        disconnectOAuthHandler,
        refreshOAuthTokenHandler,
        // Field mapping handlers
        getContactPropertiesHandler,
        saveFieldMappingsHandler,
        // Queue interval handler
        startHubSpotQueueIntervalHandler
    };
}
